import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, Chip, CircularProgress } from "@mui/material";
import Filters from "../../../../../../assets/images/filters.svg";
import Threedots from "../../../../../../assets/images/PhosphorLine.svg";
import Refresh from "../../../../../../assets/images/ArrowCounterClockwise.svg";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import {
    fetchINTREXPORT,
    fetchINTROverviewTable,
    fetchINTRRecoveryFilters,
    fetchIntrRecoveryShipmentTable,
    fetchIntrRecoveryGlobalGraph,
} from "../../../../../api/loss_to_action_engin";
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import MoreFilters from "Samarth Module/components/drawers/moreFilters";
import CustomiseColumns from "Samarth Module/components/drawers/CustomiseColumns";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";
import styles from "../intr.module.css";
import INTROverviewTable from "../intrOverviewTable";
import newExcel from "../../../../../../assets/images/FileXls.svg";
import Expand from "../../../../../../assets/images/expand.svg";
import Slider from "../../../../../../assets/images/sliders 1.svg";
import CustomButton from "Samarth Module/components/buttons/CustomButton";
import IntrRecoveryVisibilityTable from "./intrRecoveryGlobalVisibilityTable";
import IntrRecoveryGlobalGraph from "./intrRecoveryGlobalGraph";
import WeightageTopNDialog from "../../WeightageTopNDialog";

const mergeOverviewRows = (overviewData) => {
    const totalRows = Array.isArray(overviewData?.total_data)
        ? overviewData.total_data
        : [];
    const dataRows = Array.isArray(overviewData?.data) ? overviewData.data : [];
    return [...totalRows, ...dataRows];
};

const buildFilterChips = (filterData, onFilterChange = () => {}) => {
    const chips = [];
    filterData.forEach((filter, index) => {
        const selectedData = Array.isArray(filter.selectedData)
            ? filter.selectedData
            : [];

        const isMoreFilter = !filter.isFavourite && !filter.isFavorite;
        if (!isMoreFilter || selectedData.length === 0) return;

        selectedData.forEach((selectedValue, subIndex) => {
            const updatedSelectedData = selectedData.filter(
                (_, currentIndex) => currentIndex !== subIndex
            );

            chips.push({
                key: `${filter.label}-${index}-${subIndex}`,
                label: selectedValue,
                onDelete: () => onFilterChange(filter.label, updatedSelectedData),
            });
        });
    });

    return chips;
};

const resolveTimeThresholdValue = (value) => {
    if (String(value ?? "").trim() === "-") {
        return "-";
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? "-" : String(numericValue);
};

const INTRGlobalView = ({ sideViewTab }) => {
    const { accounts } = useMsal();
    const activeAccount = accounts[0];

    const [isDrawerOpen, setIsDrawerOpen] = useState({
        filterDrawer: false,
        customiseDrawer: false,
    });

    const [graphData, setGraphData] = useState([]);
    const [visibilityTableData, setVisibilityTableData] = useState([]);
    const [tableData, setTableData] = useState([]);

    const [kpi, setKPI] = useState({
        "Loss Mitigated Quantity": 0,
        "Loss Mitigated Value": 0,
    });

    const defaultWeightage = {
        Count: "0",
        Volume: "0",
        Value: "100",
    };

    const defaultTopNValue = 10;
    const defaultTimeValue = "-";

    const [openWeightageDialog, setOpenWeightageDialog] = useState(false);
    const [weightageError, setWeightageError] = useState("");
    const [dialogLoading, setDialogLoading] = useState(false);

    const [weightage, setWeightage] = useState(defaultWeightage);
    const [topNValue, setTopNValue] = useState(defaultTopNValue);
    const [timeValue, setTimeValue] = useState(defaultTimeValue);

    const previousWeightageRef = useRef(defaultWeightage);
    const previousTopNValueRef = useRef(defaultTopNValue);
    const previousTimeValueRef = useRef(defaultTimeValue);

    const [paginationData, setPaginationData] = useState(null);

    const [sortingData, setSortingData] = useState({
        sortBy: "",
        sortOrder: "ASC",
        fieldsection: "",
    });

    const [filters, setFilters] = useState({
        intrRecoveryFiltersData: [],
        intrRecoveryColumnsData: [],
    });

    const [selectedUOM, setSelectedUOM] = useState("Tonnes");

    const [loading, setLoading] = useState({
        initialLoading: true,
        dataLoading: false,
        tableBodyLoading: false,
        exportLoading: false,
    });

    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: "",
        type: "error",
    });

    const [showExpanded, setShowExpanded] = useState(false);

    const uomOptions = ["Cases", "Tonnes", "Eaches"];

    const handleWeightageDialogOpen = () => {
        previousWeightageRef.current = { ...weightage };
        previousTopNValueRef.current = topNValue;
        previousTimeValueRef.current = timeValue;
        setWeightageError("");
        setOpenWeightageDialog(true);
    };

    const handleWeightageDialogClose = () => {
        setWeightage({ ...previousWeightageRef.current });
        setTopNValue(previousTopNValueRef.current);
        setTimeValue(previousTimeValueRef.current);
        setWeightageError("");
        setOpenWeightageDialog(false);
    };

    const validateWeightage = (data, currentTopN = topNValue) => {
        if (data.Count.trim() === "") {
            setWeightageError("Count can't be empty");
            return false;
        }

        if (data.Volume.trim() === "") {
            setWeightageError("Volume can't be empty");
            return false;
        }

        if (data.Value.trim() === "") {
            setWeightageError("Value can't be empty");
            return false;
        }

        const total = Number(data.Count) + Number(data.Volume) + Number(data.Value);

        if (total !== 100) {
            setWeightageError("Sum of Count, Volume and Value must be 100%");
            return false;
        }

        if (String(currentTopN ?? "").trim() === "") {
            setWeightageError("Top N can't be empty");
            return false;
        }

        if (Number(currentTopN) <= 0) {
            setWeightageError("Top N must be greater than 0");
            return false;
        }

        if (Number(currentTopN) > 1000) {
            setWeightageError("Top N value is too large");
            return false;
        }

        setWeightageError("");
        return true;
    };

    const buildFiltersPayload = (currentFilters = filters.intrRecoveryFiltersData) =>
        currentFilters.reduce((acc, filter) => {
            acc[filter.label] = filter.selectedData;
            return acc;
        }, {});

    const buildBasePayload = (
        currentFilters = filters.intrRecoveryFiltersData,
        uom = selectedUOM
    ) => ({
        email: activeAccount.username,
        uom,
        filters: buildFiltersPayload(currentFilters),
        type: "INTR Recovery",
        "Weightage Percentage": {
            Count: Number(weightage.Count),
            Volume: Number(weightage.Volume),
            Value: Number(weightage.Value),
        },
        Time: resolveTimeThresholdValue(timeValue),
        top_n: Number(topNValue),
    });

    const fetchShipmentTableWithWeightage = async () => {
        if (!validateWeightage(weightage, topNValue)) return;

        setDialogLoading(true);

        try {
            const basePayload = buildBasePayload();

            const overviewPayload = {
                ...basePayload,
                pagination: {
                    page: paginationData?.page || 1,
                    rowsPerPage: paginationData?.rowsPerPage || 15,
                },
                sorting: {
                    sortBy: sortingData?.sortBy || "",
                    sortOrder: sortingData?.sortOrder || "ASC",
                    fieldsection: sortingData?.fieldsection || "",
                },
            };

            const shipmentPayload = {
                ...basePayload,
            };

            const graphPayload = {
                ...basePayload,
            };

            const [overviewRes, shipmentTableRes, graphRes] = await Promise.all([
                fetchINTROverviewTable(overviewPayload),
                fetchIntrRecoveryShipmentTable(shipmentPayload),
                fetchIntrRecoveryGlobalGraph(graphPayload),
            ]);

            setTableData(mergeOverviewRows(overviewRes));

            setKPI(
                overviewRes?.kpi || {
                    "Loss Mitigated Quantity": 0,
                    "Loss Mitigated Value": 0,
                }
            );

            setPaginationData(overviewRes?.pagination || null);

            setSortingData(
                overviewRes?.sorting || {
                    sortBy: sortingData?.sortBy || "",
                    sortOrder: sortingData?.sortOrder || "ASC",
                    fieldsection: sortingData?.fieldsection || "",
                }
            );

            setVisibilityTableData(shipmentTableRes?.data || []);
            setGraphData(graphRes?.data || []);

            previousWeightageRef.current = { ...weightage };
            previousTopNValueRef.current = topNValue;
            previousTimeValueRef.current = timeValue;

            setOpenWeightageDialog(false);
        } catch (error) {
            console.error(
                "Error updating overview, shipment table and graph with weightage/top N:",
                error
            );

            setSnackBarObject({
                snackbarOpen: true,
                message: "Error occurred while updating Weighted Average and Top N.",
                type: "error",
            });
        } finally {
            setDialogLoading(false);
        }
    };

    const fetchInitialData = async (
        INTRRecoveryFilters = [],
        uom = selectedUOM
    ) => {
        try {
            const sentSelections = INTRRecoveryFilters.reduce((acc, filter) => {
                acc[filter.label] = Array.isArray(filter.selectedData)
                    ? filter.selectedData
                    : [];
                return acc;
            }, {});

            const payload = {
                email: activeAccount.username,
                uom,
                filters: INTRRecoveryFilters.reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                type: "INTR Recovery",
            };

            const intrRecoveryFiltersData = await fetchINTRRecoveryFilters(payload);

            const returnedFilters = intrRecoveryFiltersData?.filters || [];

            const mergedFilters = returnedFilters.map((filter) =>
                Object.hasOwn(sentSelections, filter.label)
                    ? { ...filter, selectedData: sentSelections[filter.label] }
                    : filter
            );

            setFilters((prev) => ({
                ...prev,
                intrRecoveryFiltersData: mergedFilters,
                intrRecoveryColumnsData: intrRecoveryFiltersData?.columns || [],
            }));

            fetchData(
                mergedFilters,
                1,
                "",
                "ASC",
                uom,
                paginationData?.rowsPerPage || 15
            );
        } catch (error) {
            console.error("Error fetching initial filter data:", error);
        } finally {
            setLoading((prev) => ({ ...prev, initialLoading: false }));
        }
    };

    const fetchData = async (
        INTRRecoveryFilters = [],
        page = 1,
        sortBy = "",
        sortOrder = "ASC",
        uom = selectedUOM,
        rowsPerPage = 15,
        fieldsection = ""
    ) => {
        const isFilterChange = page === 1 && !sortBy;

        setLoading((prev) => ({
            ...prev,
            dataLoading: isFilterChange,
            tableBodyLoading: !isFilterChange,
        }));

        try {
            const payload = {
                email: activeAccount.username,
                uom,
                filters: INTRRecoveryFilters.reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                type: "INTR Recovery",
                pagination: {
                    page,
                    rowsPerPage,
                },
                sorting: {
                    sortBy,
                    sortOrder,
                    fieldsection,
                },
                "Weightage Percentage": {
                    Count: Number(weightage.Count),
                    Volume: Number(weightage.Volume),
                    Value: Number(weightage.Value),
                },
                Time: resolveTimeThresholdValue(timeValue),
                top_n: Number(topNValue),
            };

            const [data, fifoGraphData, visiTableData] = await Promise.all([
                fetchINTROverviewTable(payload),
                fetchIntrRecoveryGlobalGraph(payload),
                fetchIntrRecoveryShipmentTable(payload),
            ]);

            setGraphData(fifoGraphData?.data || []);
            setVisibilityTableData(visiTableData?.data || []);

            setTableData(mergeOverviewRows(data));

            setKPI(
                data?.kpi || {
                    "Loss Mitigated Quantity": 0,
                    "Loss Mitigated Value": 0,
                }
            );

            setPaginationData(data?.pagination || null);

            setSortingData({
                sortBy: data?.sorting?.sortBy || sortBy,
                sortOrder: data?.sorting?.sortOrder || sortOrder,
                fieldsection: data?.sorting?.fieldsection || fieldsection,
            });
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading((prev) => ({
                ...prev,
                dataLoading: false,
                tableBodyLoading: false,
            }));
        }
    };

    useEffect(() => {
        // eslint-disable-next-line no-undef
        const currentPath = globalThis?.location?.pathname || "";
        if (currentPath.includes("/loss_to_action_engine")) {
            fetchInitialData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handlePageChange = (
        newPage,
        sortCol,
        sortDir,
        rowsPerPage,
        fieldsection = ""
    ) => {
        const sortBy = sortCol || sortingData.sortBy;
        const sortOrder = sortDir || sortingData.sortOrder;

        setSortingData({ sortBy, sortOrder, fieldsection });
        setPaginationData((prev) => ({ ...prev, page: newPage, rowsPerPage }));

        fetchData(
            filters.intrRecoveryFiltersData,
            newPage,
            sortBy,
            sortOrder,
            selectedUOM,
            rowsPerPage,
            fieldsection
        );
    };

    const toggleFilterDrawer = useCallback(() => {
        setIsDrawerOpen((prev) => ({
            ...prev,
            filterDrawer: !prev.filterDrawer,
        }));
    }, []);

    const toggleCustomizeDrawer = useCallback(() => {
        setIsDrawerOpen((prev) => ({
            ...prev,
            customiseDrawer: !prev.customiseDrawer,
        }));
    }, []);

    const handleFilterChange = (label, selectedOptions) => {
        let newFilter;

        setFilters((prevFilters) => {
            newFilter = {
                ...prevFilters,
                intrRecoveryFiltersData: prevFilters.intrRecoveryFiltersData.map(
                    (filter) =>
                        filter.label === label
                            ? { ...filter, selectedData: selectedOptions }
                            : filter
                ),
            };

            return newFilter;
        });

        fetchInitialData(newFilter.intrRecoveryFiltersData, selectedUOM);
    };

    const filterChips = useMemo(
        () => buildFilterChips(filters.intrRecoveryFiltersData, handleFilterChange),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filters.intrRecoveryFiltersData]
    );

    const handleUOMChange = (_label, selectedOptions = "Tonnes") => {
        const newUOM = selectedOptions;
        setSelectedUOM(newUOM);

        fetchData(
            filters.intrRecoveryFiltersData,
            1,
            "",
            "ASC",
            newUOM,
            paginationData?.rowsPerPage || 15
        );
    };

    const resetFilter = () => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            intrRecoveryFiltersData: prevFilters.intrRecoveryFiltersData.map(
                (filter) => ({
                    ...filter,
                    selectedData: [],
                })
            ),
        }));

        setSelectedUOM("Tonnes");
        fetchInitialData([], "Tonnes");
    };

    const handleExportToExcel = async () => {
        setLoading((prev) => ({
            ...prev,
            exportLoading: true,
        }));

        try {
            const payload = {
                email: activeAccount.username,
                uom: selectedUOM,
                filters: buildFiltersPayload(),
                type: "INTR Recovery",
                "Weightage Percentage": {
                    Count: Number(weightage.Count),
                    Volume: Number(weightage.Volume),
                    Value: Number(weightage.Value),
                },
                Time: timeValue === "-" ? "-" : Number(timeValue),
                sorting: {
                    sortBy: sortingData.sortBy || "",
                    sortOrder: sortingData.sortOrder || "DESC",
                    fieldsection: sortingData.fieldsection || "",
                },
                top_n: Number(topNValue),
            };

            await fetchINTREXPORT(payload);

            setSnackBarObject({
                snackbarOpen: true,
                message: "File exported successfully",
                type: "success",
            });
        } catch (error) {
            console.error("Error exporting data:", error);

            setSnackBarObject({
                snackbarOpen: true,
                message: "Error occurred while exporting file. Please try again later.",
                type: "error",
            });
        } finally {
            setLoading((prev) => ({
                ...prev,
                exportLoading: false,
            }));
        }
    };

    const handleExpand = () => {
        setShowExpanded(!showExpanded);
    };

    const handleCloseExpanded = () => {
        setShowExpanded(false);
    };

    const handleCustomization = () => {
        toggleCustomizeDrawer();
    };

    const closeSnackbar = useCallback(() => {
        setSnackBarObject((prev) => ({ ...prev, snackbarOpen: false }));
    }, []);

    const formatIndianCurrency = (value) => {
        if (!value && value !== 0) return "0";

        const numValue = typeof value === "string" ? Number.parseFloat(value) : value;

        if (Number.isNaN(numValue)) return "0";

        return numValue.toLocaleString("en-IN", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
        });
    };

    return (
        <>
            {loading.initialLoading ? (
                <div className={styles.loaderContainer}>
                    <CircularProgress />
                </div>
            ) : (
                <div
                    className={`${styles.container} ${
                        loading.exportLoading ? styles.blurBackground : ""
                    }`}
                >
                    <div className={styles.fixedFilterSection}>
                        <div className={styles.sectionContainer}>
                            {filters.intrRecoveryFiltersData
                                .filter((filter) => filter.isFavourite)
                                .map((filter, index) => (
                                    <div
                                        key={`${filter.label}-${index}`}
                                        className={styles.filterWrapper}
                                    >
                                        <MultiSelectWithSearchInput
                                            key={`${filter.label}-${index}`}
                                            label={filter.label}
                                            options={filter.data}
                                            filterValues={filter.selectedData}
                                            isMultiSelect={filter.isMultiSelect}
                                            handleFilter={handleFilterChange}
                                            type="INTR Recovery"
                                            defaultValue={filter.defaultValue || []}
                                        />
                                    </div>
                                ))}

                            <div className={styles.filterWrapper}>
                                <MultiSelectWithSearchInput
                                    label="Units"
                                    options={uomOptions}
                                    filterValues={[selectedUOM]}
                                    isMultiSelect={false}
                                    handleFilter={handleUOMChange}
                                    type="INTR Recovery"
                                    defaultValue="Tonnes"
                                />
                            </div>

                            <div className={styles.iconContainer}>
                                <button
                                    type="button"
                                    onClick={resetFilter}
                                    className={styles.iconStyling}
                                    style={{ border: "none", padding: 0, display: "inline-flex" }}
                                    aria-label="Refresh filter"
                                >
                                    <img
                                        src={Refresh}
                                        className={styles.iconStyling}
                                        alt="refresh filter"
                                    />
                                </button>

                                <OverflowMenu
                                    menuItems={[
                                        {
                                            label: "More Filters",
                                            icon: Filters,
                                            onClick: toggleFilterDrawer,
                                        },
                                    ]}
                                    Icon={Threedots}
                                />
                            </div>
                        </div>

                        {filterChips.length > 0 && (
                            <div className={styles.filtersBtnContainer}>
                                <Box className={styles.chipContainer}>
                                    {filterChips.map((chip) => (
                                        <Chip
                                            key={chip.key}
                                            label={chip.label}
                                            onDelete={chip.onDelete}
                                        />
                                    ))}
                                </Box>
                            </div>
                        )}
                    </div>

                    <div className={styles.scrollableContent}>
                        {loading.dataLoading ? (
                            <div className={styles.loaderContainer}>
                                <CircularProgress />
                            </div>
                        ) : (
                            <div>
                                <div
                                    style={{
                                        padding: "15px",
                                        background: "white",
                                        marginBottom: "10px",
                                        maxHeight: "430px",
                                    }}
                                >
                                    <div className={styles.graphWrapper}>
                                        <div className={styles.graphCard}>
                                            <span className={styles.cardTitle}>
                                                Order Loss by Value Graph

                                                <div className={styles.simpleTooltip}>
                                                    <button
                                                        className={styles.tooltipButton}
                                                        onClick={handleWeightageDialogOpen}
                                                    >
                                                        Weighted Average & Top N
                                                    </button>

                                                    <span className={styles.tooltipText}>
                                                        Weighted Average defines priority score using
                                                        Count, Volume & Value.
                                                        <br />
                                                        Top N controls how many high-priority records
                                                        are shown.
                                                    </span>
                                                </div>
                                            </span>

                                            <div className={styles.graphInner}>
                                                <IntrRecoveryGlobalGraph
                                                    data={graphData}
                                                    topNValue={topNValue}
                                                />
                                            </div>
                                        </div>

                                        <div className={styles.graphCard}>
                                            <span className={styles.cardTitle}>
                                                Order Loss by Value Shipment Table
                                            </span>

                                            <IntrRecoveryVisibilityTable
                                                data={visibilityTableData}
                                                topNValue={topNValue}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <WeightageTopNDialog
                                    open={openWeightageDialog}
                                    onClose={handleWeightageDialogClose}
                                    weightage={weightage}
                                    setWeightage={setWeightage}
                                    topNValue={topNValue}
                                    setTopNValue={setTopNValue}
                                    weightageError={weightageError}
                                    validateWeightage={validateWeightage}
                                    dialogLoading={dialogLoading}
                                    onSubmit={fetchShipmentTableWithWeightage}
                                    showTimeThreshold={true}
                                    timeValue={timeValue}
                                    setTimeValue={setTimeValue}
                                    timeLabel="INTR Aging (hrs)"
                                />

                                <div className={styles.intrMainBody}>
                                    <div className={styles.intrHeader}>
                                        <div>
                                            <span>Overview</span>
                                            <span className={styles.intrKPI}>
                                                <span className={styles.kpiHeading}>
                                                    Loss Mitigated Quantity:
                                                </span>
                                                &nbsp;
                                                <span className={styles.kpiValue}>
                                                    {formatIndianCurrency(
                                                        kpi["Loss Mitigated Quantity"]
                                                    )}
                                                </span>
                                                &nbsp;
                                                <span aria-hidden="true">|</span>
                                                &nbsp;
                                                <span className={styles.kpiHeading}>
                                                    Loss Mitigated Value:
                                                </span>
                                                &nbsp;
                                                <span className={styles.kpiValue}>₹{formatIndianCurrency(kpi["Loss Mitigated Value"])}</span>
                                            </span>
                                        </div>

                                        <div className={styles.customButtonGroup}>
                                            <CustomButton
                                                icon={Slider}
                                                label="Customise"
                                                type="mm-save"
                                                onClick={handleCustomization}
                                            />

                                            <CustomButton
                                                icon={newExcel}
                                                label="Export"
                                                type="mm-save"
                                                onClick={handleExportToExcel}
                                            />

                                            <div className={styles.segmentedBtn}>
                                                <button
                                                    type="button"
                                                    className={styles.segment}
                                                    style={{
                                                        padding: "6px",
                                                        background: "white",
                                                        border: "1px solid #e5e7eb",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={handleExpand}
                                                    title="Expand / Zoom"
                                                >
                                                    <img
                                                        src={Expand}
                                                        alt="Expand"
                                                        className={styles.icon}
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <INTROverviewTable
                                        data={tableData}
                                        columns={filters.intrRecoveryColumnsData}
                                        paginationData={paginationData}
                                        sortingData={sortingData}
                                        handlePageChange={handlePageChange}
                                        isLoading={loading.tableBodyLoading}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {isDrawerOpen.filterDrawer && (
                        <MoreFilters
                            anchor="right"
                            filterDrawer={isDrawerOpen.filterDrawer}
                            handleFilterDrawer={toggleFilterDrawer}
                            rmpmFilters={filters.intrRecoveryFiltersData}
                            type="INTR Recovery"
                            setRmpmFilters={(updatedFilters) => {
                                setFilters((prevFilters) => ({
                                    ...prevFilters,
                                    intrRecoveryFiltersData: updatedFilters,
                                }));
                                fetchInitialData(updatedFilters);
                            }}
                            username={activeAccount.username}
                            limit={9}
                        />
                    )}

                    {isDrawerOpen.customiseDrawer && (
                        <CustomiseColumns
                            anchor="right"
                            columns={[...filters.intrRecoveryColumnsData]}
                            isOpen={isDrawerOpen.customiseDrawer}
                            handleCustomizeDrawer={toggleCustomizeDrawer}
                            columnCustomization={(customizedColumns) => {
                                setFilters((prevFilters) => ({
                                    ...prevFilters,
                                    intrRecoveryColumnsData: customizedColumns,
                                }));
                                toggleCustomizeDrawer();
                            }}
                            username={activeAccount.username}
                            type="INTR Recovery"
                        />
                    )}
   
            </div>
            )}

            {loading.exportLoading && (
                <div className={styles.exportOverlay}>
                    <div className={styles.exportLoader}>
                        <CircularProgress />
                        <div style={{ marginTop: 10, fontSize: 14 }}>
                            Exporting file...
                        </div>
                    </div>
                </div>
            )}

            {showExpanded && (
                <div className={styles.expandedOverlay}>
                    <div className={styles.expandedContainer}>
                        <div className={styles.expandedHeader}>
                            <span>INTR Overview</span>
                            <button
                                className={styles.closeButton}
                                onClick={handleCloseExpanded}
                            >
                                ✕
                            </button>
                        </div>

                        <div className={styles.expandedTableWrapper}>
                            <INTROverviewTable
                                data={tableData}
                                columns={filters.intrRecoveryColumnsData}
                                paginationData={paginationData}
                                sortingData={sortingData}
                                handlePageChange={handlePageChange}
                                isLoading={loading.tableBodyLoading}
                            />
                        </div>
                    </div>
                </div>
            )}

            {snackBarObject.snackbarOpen && (
                <SnackBarMessage
                    type={snackBarObject.type}
                    snackbarMessage={snackBarObject.message}
                    snackbarOpen={snackBarObject.snackbarOpen}
                    setSnackbarOpen={closeSnackbar}
                />
            )}
        </>
    );
};

INTRGlobalView.propTypes = {
    sideViewTab: PropTypes.string,
};

export default INTRGlobalView;